import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { PreviewSection, LookupField, ClockInOptions } from "../shared";
import { LookupInput, type LookupInputOption } from "@/components/ui/LookupInput";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ContactInput } from "@/components/inputs/ContactInput";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { AbilitiesInput } from "@/components/inputs/AbilitiesInput";
import { RestrictionsInput } from "@/components/inputs/RestrictionsInput";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ProjectType, PROJECT_VENUE_TYPES, ProjectVenueType, NoteType } from "@/types/db/projects";
import useDateTime from "@/hooks/useDateTime";
import { useTheme } from "@/hooks/useTheme";
import Modal from "@/components/Modal";
import { ModalCard } from "@/components/Modals/ModalCard";
import { supabase } from "@/lib/supabase-client";
import Chip from "@/components/ui/Chip";
import { Button } from "@/components/ui/Button";
import { PdfCanvas } from "@/components/Pdf/PdfCanvas";
import { shiftsService } from "@/services/shiftService";
import { useModal } from "@/hooks/useModal";
import useToast from "@/hooks/useToast";
import { tenantService } from "@/services/tenantService";

import { NoteEditor, Note } from "@/components/Editors";
import useAuth from "@/hooks/useAuth";
import { userService } from "@/services/userService";
import { MaterialCommunityIcons } from "@expo/vector-icons";

export interface ProjectOverviewTabProps {
  project: ProjectType | null;
  loading?: boolean;
}

export function ProjectOverviewTab({ project, loading = false }: ProjectOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<ProjectType>();
  const { formatTimestamp } = useDateTime();
  const { colors } = useTheme();
  const { user, displayNames } = useAuth();
  const { openModal, closeModal } = useModal();
  const toast = useToast();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [tenantName, setTenantName] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const tenantData = await tenantService.getTenantNameAndMeta();
        if (!cancel) setTenantName(tenantData?.name ?? "");
      } catch (_err) {
        // ignore
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // Modal states
  const [noteEditorVisible, setNoteEditorVisible] = useState(false);

  // Current user name for notes
  const [currentUserName, setCurrentUserName] = useState<string>("Unknown");
  // Positions for note display
  const [positions, setPositions] = useState<{ id: string; name: string }[]>([]);

  // Use formData when editing, otherwise use project data
  const displayData = editing ? formData : project;
  // Load current user name for notes
  useEffect(() => {
    const loadCurrentUser = async () => {
      if (!user?.id) return;
      try {
        const userData = await userService.getUserById(user.id);
        if (userData) {
          setCurrentUserName(`${userData.first_name || ""} ${userData.last_name || ""}`.trim() || "Unknown");
        }
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };
    loadCurrentUser();
  }, [user?.id]);

  // Fetch positions for note display
  useEffect(() => {
    const fetchPositions = async () => {
      const { data } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .order("name", { ascending: true });
      setPositions(data || []);
    };
    fetchPositions();
  }, []);

  // ---------- location market (inherited) ----------
  const [locationMarket, setLocationMarket] = useState<{ id: string; name: string; fips_code?: string } | null>(null);

  useEffect(() => {
    const loadLocationMarket = async () => {
      const locationId = displayData?.location_id;
      if (!locationId) {
        setLocationMarket(null);
        return;
      }
      const { data: location } = await supabase
        .schema("lists")
        .from("locations")
        .select("market_id")
        .eq("id", locationId)
        .single();
      if (!location?.market_id) {
        setLocationMarket(null);
        return;
      }
      const { data: market } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name, fips_code")
        .eq("id", location.market_id)
        .maybeSingle();
      setLocationMarket(market || null);
    };
    loadLocationMarket();
  }, [displayData?.location_id]);

  // ---------- location options (fetched once on mount) ----------
  const [locationOptions, setLocationOptions] = useState<LookupInputOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadLocations = async () => {
      try {
        const { data, error } = await supabase
          .schema("lists")
          .from("locations")
          .select("id, name, street, city, state, zip")
          .order("name", { ascending: true });
        if (error) throw error;
        if (cancelled) return;
        setLocationOptions(
          (data ?? []).map((loc: any) => ({
            id: loc.id,
            name: loc.name,
            subtitle: [loc.street, loc.city, loc.state, loc.zip].filter(Boolean).join(", ") || undefined,
            icon: "map-marker" as const,
          })),
        );
      } catch (err) {
        console.error("Failed to load locations:", err);
      }
    };
    loadLocations();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- dress code options (fetched once on mount) ----------
  const [dressCodeOptions, setDressCodeOptions] = useState<LookupInputOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadDressCodes = async () => {
      try {
        const { data, error } = await supabase
          .schema("lists")
          .from("dress_codes")
          .select("id, name, description")
          .order("name", { ascending: true });
        if (error) throw error;
        if (cancelled) return;
        setDressCodeOptions(
          (data ?? []).map((dc: any) => ({
            id: dc.id,
            name: dc.name,
            subtitle: dc.description || undefined,
            icon: "tshirt-crew" as const,
          })),
        );
      } catch (err) {
        console.error("Failed to load dress codes:", err);
      }
    };
    loadDressCodes();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLocationSelect = useCallback(
    async (locationId: string | null) => {
      if (!locationId) {
        updateFormData({
          ...formData,
          location_id: undefined,
          location: undefined,
        });
        return;
      }
      try {
        const { data: location, error } = await supabase
          .schema("lists")
          .from("locations")
          .select("id, name, street, city, state, zip")
          .eq("id", locationId)
          .single();
        if (error) throw error;
        updateFormData({
          ...formData,
          location_id: location.id,
          location: {
            id: location.id,
            name: location.name,
            address: {
              street: location.street,
              city: location.city,
              state: location.state,
              zip: location.zip,
            },
          },
        });
      } catch (err) {
        console.error("Failed to load location:", err);
      }
    },
    [formData, updateFormData],
  );

  const handleDressCodeSelect = useCallback(
    (dressCodeId: string | null) => {
      if (!dressCodeId) {
        updateFormData({
          ...formData,
          meta_data: {
            ...formData?.meta_data,
            dress_code_id: undefined,
          },
          dress_code: undefined,
        });
        return;
      }
      const opt = dressCodeOptions.find((o) => o.id === dressCodeId);
      updateFormData({
        ...formData,
        meta_data: {
          ...formData?.meta_data,
          dress_code_id: dressCodeId,
        },
        dress_code: {
          id: dressCodeId,
          name: opt?.name ?? "",
          description: opt?.subtitle,
        },
      });
    },
    [formData, updateFormData, dressCodeOptions],
  );

  const DISCLAIMER = `DISCLAIMER: RockForce will only communicate with Client's designated representatives as outlined in the Agreement between RockForce and Client. RockForce promises in good faith to provide to the best of its ability Crew Members (the "Services") to Client's Calls in accordance with the terms of the Agreement between RockForce and Client. RockForce's Services are provided with the understanding the Crew Members are under Client's (or a third-party designee's of Client) authority, direction, control, and supervision. RockForce disclaims all responsibility or liability for all costs, penalties, fees, inconveniences, or cancellations due to RockForce's inability to perform services pursuant to the terms of the Agreement between Client and RockForce. This Call Confirmation is subject to the terms & conditions at https://www.qsrproductionsllc.com/terms-and-conditions under the extant Agreement between Client and RockForce, and such terms and conditions are fully incorporated within this Call Confirmation as if fully set forth herein.`;

  const shiftLabel = displayNames?.shifts?.name || "Shift";
  const shiftsLabel = displayNames?.shifts?.plural || "Shifts";

  const formatDateShort = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const formatTimeShort = (iso: string | null): string => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const formatMealBreak = (mealOptions: any[]): string => {
    if (!mealOptions?.length) return "None";
    return mealOptions
      .map((m: any) => {
        // const type = m.type === "PAID" ? "Paid" : "Unpaid";
        const dur = m.duration || 30;
        return `${dur} min`;
      })
      .join(", ");
  };

  const handleViewConfirmationPDF = useCallback(async () => {
    if (!project?.id) return;

    setIsGeneratingPDF(true);
    try {
      const shifts = await shiftsService.getShiftsByProjectId(project.id);

      const dressCodesMap = new Map<string, string>();
      const dressCodeIds = new Set<string>();
      shifts.forEach((s: any) => {
        const dcId = s.meta_data?.dress_code_id;
        if (dcId) dressCodeIds.add(dcId);
      });
      if (dressCodeIds.size > 0) {
        const { data: dcs } = await supabase
          .schema("lists")
          .from("dress_codes")
          .select("id, name")
          .in("id", Array.from(dressCodeIds));
        dcs?.forEach((dc: any) => dressCodesMap.set(dc.id, dc.name));
      }

      const sortedShifts = [...shifts].sort(
        (a: any, b: any) => new Date(a.start_time || 0).getTime() - new Date(b.start_time || 0).getTime(),
      );

      const confirmationShifts = sortedShifts.map((s: any) => ({
        name: s.name || "Untitled",
        date: formatDateShort(s.start_time),
        time: `${formatTimeShort(s.start_time)} - ${formatTimeShort(s.end_time)}`,
        positions: (s.shift_positions || []).map((sp: any) => ({
          positionName: sp.position?.name || "Unknown",
          qtyPreferred: sp.quantity || 0,
        })),
        estDuration:
          s.start_time && s.end_time
            ? `${((new Date(s.end_time).getTime() - new Date(s.start_time).getTime()) / 3600000).toFixed(1)} hrs`
            : "—",
        mealBreak: formatMealBreak(s.meta_data?.meal_option),
        dressCode: s.meta_data?.dress_code_id ? dressCodesMap.get(s.meta_data.dress_code_id) || "—" : "—",
        venueType: s.meta_data?.venue_type || "—",
        onsiteContactName: s.meta_data?.onsite_contact_name || s.onsite_contact_details?.name || "—",
        onsiteContactPhone: s.onsite_contact_details?.phone_number || "—",
      }));

      const startDate = sortedShifts.length ? formatDateShort(sortedShifts[0].start_time as string | null) : "";
      const endDate = sortedShifts.length
        ? formatDateShort(sortedShifts[sortedShifts.length - 1].end_time as string | null)
        : "";
      const eventId = (project.id || "").replace(/-/g, "").slice(0, 8).toUpperCase();

      const pdfData = {
        action: "confirmation",
        title: `${shiftLabel} Confirmation`,
        disclaimer: DISCLAIMER,
        eventName: project.name || "",
        startDate,
        endDate,
        eventId,
        shifts: confirmationShifts,
        shiftLabel,
        shiftsLabel,
        // companyName: tenantName || undefined,
        companyName: "RockForce",
      };

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("No authentication token available");

      const response = await fetch(`${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/pdf-create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(pdfData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Edge function error: ${errorText}`);
      }

      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        openModal(
          <ModalCard>
            <View style={{ width: 800, height: 900 }}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: 16,
                  borderBottomWidth: StyleSheet.hairlineWidth,
                  borderBottomColor: colors.border,
                }}
              >
                <ThemedText type="subtitle">{shiftLabel} Confirmation</ThemedText>
                <View style={{ flexDirection: "row", gap: 8 }}>
                  <Button
                    text="Download"
                    icon="download"
                    onPress={() => {
                      const a = document.createElement("a");
                      a.href = dataUrl;
                      a.download = `confirmation-${eventId}.pdf`;
                      a.click();
                    }}
                    style={{ height: 32, paddingHorizontal: 12, backgroundColor: colors.primary }}
                    textStyle={{ color: "white", fontSize: 13 }}
                  />
                  <Button
                    text="Close"
                    onPress={closeModal}
                    style={{
                      height: 32,
                      paddingHorizontal: 12,
                      borderWidth: 1,
                      borderColor: colors.border,
                      backgroundColor: "transparent",
                    }}
                    textStyle={{ fontSize: 13 }}
                  />
                </View>
              </View>
              <PdfCanvas src={dataUrl} loading={false} dirty={false} />
            </View>
          </ModalCard>,
        );
      };
      reader.readAsDataURL(blob);
    } catch (error: any) {
      console.error("Confirmation PDF error:", error);
      toast.showToast({
        title: "PDF Generation Failed",
        message: error.message || "Failed to generate confirmation PDF",
        toastType: "error",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, displayNames, tenantName, openModal, closeModal, colors, toast]);

  const computedDates = project?.shifts?.length
    ? {
        start_time: new Date(Math.min(...project.shifts.map((s) => new Date(s.start_time).getTime()))).toISOString(),
        end_time: new Date(Math.max(...project.shifts.map((s) => new Date(s.end_time).getTime()))).toISOString(),
      }
    : { start_time: null, end_time: null };

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 16 }}>
        {/* Project Details */}
        <PreviewSection title="">
          {/* Read-only computed fields */}
          <LookupField
            label="Start Date"
            value={computedDates.start_time ? formatTimestamp(computedDates.start_time, false) : "—"}
            placeholder="No shifts scheduled"
            readonly
            loading={loading}
          />
          <LookupField
            label="End Date"
            value={computedDates.end_time ? formatTimestamp(computedDates.end_time, false) : "—"}
            placeholder="No shifts scheduled"
            readonly
            loading={loading}
          />
          <ClientInput
            value={project?.client_id ?? null}
            state={loading ? "loading" : "idle"}
            label={displayNames?.clients?.name || "Client"}
            placeholder={`No ${displayNames?.clients?.name?.toLowerCase() || "client"}`}
          />

          {/* Contact fields */}
          <ContactInput
            value={displayData?.requested_by ?? null}
            clientId={displayData?.client_id}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            label="Requested By"
            onChange={(id) => updateFormData({ ...formData, requested_by: id ?? undefined })}
          />
          <ContactInput
            value={(displayData as any)?.primary_contact ?? null}
            clientId={displayData?.client_id}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            label="Primary Contact"
            onChange={(id) => updateFormData({ ...formData, primary_contact: id ?? undefined } as any)}
          />
          <ContactInput
            value={(displayData as any)?.onsite_contact ?? null}
            clientId={displayData?.client_id}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            label="Onsite Contact"
            onChange={(id) => updateFormData({ ...formData, onsite_contact: id ?? undefined } as any)}
          />
          <LookupInput
            label="Location"
            value={displayData?.location_id ?? null}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            icon="map-marker"
            placeholder="No location selected"
            options={locationOptions}
            onChange={handleLocationSelect}
          />
          <LookupInput
            label="Dress Code"
            value={displayData?.meta_data?.dress_code_id ?? null}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            icon="tshirt-crew"
            placeholder="No dress code selected"
            options={dressCodeOptions}
            onChange={handleDressCodeSelect}
          />
          <LookupInput
            label="Venue Type"
            value={displayData?.meta_data?.venue_type || null}
            options={PROJECT_VENUE_TYPES.map((vt) => ({ id: vt, name: vt, icon: "home-city" as const }))}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            icon="home-city"
            placeholder="Select venue type"
            onChange={(value) => {
              updateFormData({
                ...formData,
                meta_data: {
                  ...formData?.meta_data,
                  venue_type: value as ProjectVenueType,
                },
              });
            }}
          />
        </PreviewSection>

        {/* Clock-In Options */}
        <ClockInOptions
          label={`${displayNames?.shifts?.name || "Shift"} Clock-In Method`}
          values={displayData?.meta_data?.clock_in_options || {}}
          editing={editing}
          onChange={(newValues) => {
            updateFormData({
              ...formData,
              meta_data: {
                ...formData?.meta_data,
                clock_in_options: newValues,
              },
            });
          }}
        />

        {/* Abilities */}
        <AbilitiesInput
          value={displayData?.project_abilities || []}
          inherited={displayData?.client?.client_abilities || []}
          inheritedLabel={`From ${displayNames?.clients?.name || "Client"}`}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(abilities) => {
            updateFormData({ ...formData, project_abilities: abilities });
          }}
        />

        {/* Restrictions */}
        <RestrictionsInput
          value={displayData?.project_restrictions || []}
          inherited={displayData?.client?.client_restrictions || []}
          inheritedLabel={`From ${displayNames?.clients?.name || "Client"}`}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(restrictions) => {
            updateFormData({ ...formData, project_restrictions: restrictions });
          }}
        />

        {/* Markets */}
        <MarketsInput
          value={displayData?.meta_data?.markets || []}
          inheritedMarket={locationMarket}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(marketIds) => {
            updateFormData({
              ...formData,
              meta_data: {
                ...formData?.meta_data,
                markets: marketIds,
              },
            });
          }}
        />

        {/* Deposit Amount */}
        <CurrencyInput
          label="Deposit Amount"
          value={displayData?.meta_data?.deposit_amount ?? null}
          editable={editing}
          loading={loading}
          onChange={(val) => {
            updateFormData({
              ...formData,
              meta_data: {
                ...formData?.meta_data,
                deposit_amount: val,
              },
            });
          }}
        />

        {/* Notes */}
        <PreviewSection
          title="Notes"
          button={
            editing
              ? {
                  iconPosition: "right",
                  icon: "plus",
                  onPress: () => setNoteEditorVisible(true),
                }
              : undefined
          }
          editing={editing}
        >
          {(displayData?.meta_data?.notes?.length || 0) === 0 ? (
            <View style={{ padding: 12, alignItems: "center" }}>
              <MaterialCommunityIcons name="note-text-outline" size={24} color={colors.textSecondary} />
              <ThemedText style={{ color: colors.textSecondary, marginTop: 4, fontSize: 12 }}>No notes</ThemedText>
            </View>
          ) : (
            <View style={{ gap: 8 }}>
              {displayData?.meta_data?.notes?.map((note: NoteType) => (
                <Pressable
                  key={note.id}
                  style={{
                    padding: 10,
                    backgroundColor: colors.backgroundSecondary,
                    borderRadius: 8,
                    borderWidth: StyleSheet.hairlineWidth,
                    borderColor: colors.border,
                  }}
                  onPress={editing ? () => setNoteEditorVisible(true) : undefined}
                >
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                    <ThemedText style={{ fontSize: 12, fontWeight: "600" }}>{note.author_name}</ThemedText>
                    <ThemedText style={{ fontSize: 11, color: colors.textSecondary }}>
                      {formatTimestamp(note.created_at, false)}
                    </ThemedText>
                  </View>
                  <ThemedText style={{ fontSize: 13 }} numberOfLines={2}>
                    {note.message}
                  </ThemedText>
                  {note.position_ids && note.position_ids.length > 0 && (
                    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                      {note.position_ids.length === positions.length && positions.length > 0 ? (
                        <Chip text="All Positions" style={{ paddingHorizontal: 6, paddingVertical: 1 }} />
                      ) : (
                        note.position_ids.map((posId: string) => {
                          const pos = positions.find((p) => p.id === posId);
                          return (
                            <Chip
                              key={posId}
                              text={pos?.name || posId}
                              style={{ paddingHorizontal: 6, paddingVertical: 1 }}
                            />
                          );
                        })
                      )}
                    </View>
                  )}
                </Pressable>
              ))}
            </View>
          )}
        </PreviewSection>

        {/* Confirmation PDF */}
        {!editing && (
          <View style={styles.pdfSection}>
            <Button
              text={isGeneratingPDF ? "Generating PDF..." : `${shiftLabel} Confirmation`}
              iconPosition="right"
              icon="file-pdf-box"
              onPress={handleViewConfirmationPDF}
              enabled={!isGeneratingPDF}
              style={[styles.pdfButton, { backgroundColor: colors.red?.background || "#fee2e2" }]}
              textStyle={{ color: colors.red?.text || "#dc2626" }}
            />
          </View>
        )}
      </ScrollView>

      {/* Note Editor */}
      {noteEditorVisible && (
        <Modal onUnmount={() => setNoteEditorVisible(false)}>
          <ModalCard>
            <NoteEditor
              notes={(formData?.meta_data?.notes as Note[]) || []}
              currentUserId={user?.id || ""}
              currentUserName={currentUserName}
              onSave={(notes) => {
                updateFormData({
                  ...formData,
                  meta_data: {
                    ...formData?.meta_data,
                    notes: notes as NoteType[],
                  },
                });
                setNoteEditorVisible(false);
              }}
              onCancel={() => setNoteEditorVisible(false)}
            />
          </ModalCard>
        </Modal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  pdfSection: {
    marginTop: 8,
    paddingTop: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#e5e7eb",
  },
  pdfButton: {
    paddingVertical: 12,
    borderRadius: 8,
  },
});
